import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, addDoc, Timestamp, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import Sidebar from './Sidebar';
import Header from './Header';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencilAlt, faTrashAlt, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import Alert from './Alert';

const Members = ({ user, onLogout }) => {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSidebarOpen, setSidebarOpen] = useState(false);
    const [isModalOpen, setModalOpen] = useState(false);
    const [newMember, setNewMember] = useState({ name: '', username: '', phoneNumber: '', birthDate: '', gender: '' });
    const [alert, setAlert] = useState({ show: false, message: '', type: '' });



    const toggleSidebar = () => {
        setSidebarOpen(!isSidebarOpen);
    };

    const toggleModal = () => {
        setModalOpen(!isModalOpen);
        setAlert({ show: false, message: '', type: '' });
    };

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setNewMember({ ...newMember, [id]: value });
    };

    const handlePhoneChange = (value) => {
        setNewMember(prev => ({ ...prev, phoneNumber: value }));
    };



    const handleAddMember = async () => {
        if (!newMember.name || !newMember.username || !newMember.phoneNumber || !newMember.gender) {
            setAlert({ show: true, message: "Nama, Username, Telepon, dan Jenis Kelamin wajib diisi!", type: 'error' });
            return;
        }

        const isDuplicateUsername = members.some(member => member.username === newMember.username);
        if (isDuplicateUsername) {
            setAlert({ show: true, message: "Username sudah terdaftar!", type: 'error' });
            return;
        }

        const isDuplicatePhone = members.some(member => member.phoneNumber === newMember.phoneNumber);
        if (isDuplicatePhone) {
            setAlert({ show: true, message: "Nomor telepon sudah terdaftar!", type: 'error' });
            return;
        }

        let birthDateTimestamp = null;
        if (newMember.birthDate) {
            const [year, month, day] = newMember.birthDate.split('-').map(Number);
            birthDateTimestamp = Timestamp.fromDate(new Date(year, month - 1, day));
        }

        try {
            const docRef = await addDoc(collection(db, "users"), {
                name: newMember.name,
                username: newMember.username,
                phoneNumber: newMember.phoneNumber,
                roles: ['member'],
                birthDate: birthDateTimestamp,
                gender: newMember.gender,
                createdAt: new Date(),
                phoneNumberVerifiedAt: null
            });
            console.log("Document written with ID: ", docRef.id);
            setMembers([...members, {
                id: docRef.id,
                id: docRef.id,
                name: newMember.name,
                username: newMember.username,
                phoneNumber: newMember.phoneNumber,
                roles: ['member'],
                birthDate: birthDateTimestamp,
                gender: newMember.gender,
                createdAt: new Date()
            }]);
            setNewMember({ name: '', username: '', phoneNumber: '', birthDate: '', gender: '' });
            toggleModal();
            window.location.reload();
        } catch (e) {
            console.error("Error adding document: ", e);
        }
    };

    useEffect(() => {
        const fetchMembers = async () => {
            try {
                const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
                const querySnapshot = await getDocs(q);
                const membersData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setMembers(membersData);
            } catch (error) {
                console.error("Error fetching members: ", error);
            }
            setLoading(false);
        };

        fetchMembers();
    }, []);

    const formatDays = (createdAt) => {
        if (createdAt && createdAt.toDate) {
            const days = Math.ceil(Math.abs(new Date() - createdAt.toDate()) / (1000 * 60 * 60 * 24));
            return `${days.toLocaleString('en-US')} hari`;
        }
        return '';
    };



    return (
        <div id="admin-container" className="flex">
            {isSidebarOpen && <div className="fixed inset-0 bg-black opacity-50 z-30 lg:hidden" onClick={toggleSidebar}></div>}
            <Sidebar isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
            <main id="main-content" className={`flex-1 lg:ml-64 transition-all duration-300 ease-in-out ${isSidebarOpen ? 'lg:ml-64' : 'lg:ml-0'}`}>
                <Header user={user} onLogout={onLogout} toggleSidebar={toggleSidebar} title="Anggota" />
                <div className="p-6">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-semibold">Daftar Anggota</h2>
                        <button onClick={toggleModal} className="bg-brand-lime text-brand-dark font-bold py-2 px-4 rounded-lg hover:bg-brand-lime-dark transition">
                            Tambah Anggota
                        </button>
                    </div>
                    {loading ? (
                        <div>Loading...</div>
                    ) : (
                        <div className="bg-white p-6 rounded-lg shadow-md">
                            {/* The container below uses overflow-x-auto to make the table responsive.
                                On smaller screens, the table will be horizontally scrollable. */}
                            <div className="block overflow-x-auto">
                                <table className="min-w-full bg-white">
                                    <thead className="bg-gray-200 text-gray-600 uppercase text-sm leading-normal">
                                        <tr>
                                            <th className="py-3 px-6 text-left">Nama</th>
                                            <th className="py-3 px-6 text-left">Username</th>
                                            <th className="py-3 px-6 text-left">Telepon</th>
                                            <th className="py-3 px-6 text-left">Jenis Kelamin</th>
                                            <th className="py-3 px-6 text-left">Tanggal Lahir</th>
                                            <th className="py-3 px-6 text-left">Lama Bergabung</th>
                                            <th className="py-3 px-6 text-center">Aksi</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-gray-600 text-sm font-light">
                                        {members.map(member => (
                                            <tr key={member.id} className="border-b border-gray-200 hover:bg-gray-100">
                                                <td className="py-3 px-6 text-left whitespace-nowrap">{member.name || member.phoneNumber} {member.roles && member.roles.includes('admin') && <span className="bg-red-500 text-white text-xs font-semibold ml-2 px-2.5 py-0.5 rounded-full">Admin</span>}</td>
                                                <td className="py-3 px-6 text-left whitespace-nowrap">{member.username || '-'}</td>
                                                <td className="py-3 px-6 text-left">
                                                    {member.phoneNumber}
                                                    {!member.phoneNumberVerifiedAt && (
                                                        <div className="relative group ml-2 inline-block">
                                                            <span className="text-yellow-500 cursor-help">
                                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                            </span>
                                                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-max px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 pointer-events-none">
                                                                Nomor Telepon Belum Terverifikasi
                                                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="py-3 px-6 text-left">{member.gender}</td>
                                                <td className="py-3 px-6 text-left">{member.birthDate && member.birthDate.toDate ? member.birthDate.toDate().toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric' }) : ''}</td>
                                                <td className="py-3 px-6 text-left">{formatDays(member.createdAt)}</td>
                                                <td className="py-3 px-6 text-center">
                                                    <div className="flex item-center justify-center">
                                                        <button className="w-4 mr-2 transform hover:text-purple-500 hover:scale-110">
                                                            <FontAwesomeIcon icon={faPencilAlt} />
                                                        </button>
                                                        <button className="w-4 mr-2 transform hover:text-red-500 hover:scale-110">
                                                            <FontAwesomeIcon icon={faTrashAlt} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                {isModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white p-8 rounded-lg shadow-lg w-1/3">
                            <h2 className="text-2xl font-bold mb-4">Tambah Anggota</h2>
                            {alert.show && (
                                <div className="mb-4">
                                    <Alert message={alert.message} type={alert.type} />
                                </div>
                            )}
                            <form>
                                <div className="mb-4">
                                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
                                        Nama
                                    </label>
                                    <input onChange={handleInputChange} value={newMember.name} className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline" id="name" type="text" placeholder="Nama" required />
                                </div>
                                <div className="mb-4">
                                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="username">
                                        Username
                                    </label>
                                    <input onChange={handleInputChange} value={newMember.username} className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline" id="username" type="text" placeholder="Username" required />
                                </div>
                                <div className="mb-4">
                                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="phoneNumber">
                                        Telepon
                                    </label>
                                    <div className="phone-input-container relative z-10 shadow appearance-none border border-gray-300 rounded w-full py-2 px-3 text-gray-700 leading-tight focus-within:shadow-outline bg-white">
                                        <PhoneInput
                                            international
                                            defaultCountry="ID"
                                            value={newMember.phoneNumber}
                                            onChange={handlePhoneChange}
                                        />
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="birthDate">
                                        Tanggal Lahir
                                    </label>
                                    <input onChange={handleInputChange} value={newMember.birthDate} className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline" id="birthDate" type="date" placeholder="Tanggal Lahir" required />
                                </div>
                                <div className="mb-4">
                                    <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="gender">
                                        Jenis Kelamin
                                    </label>
                                    <select onChange={handleInputChange} value={newMember.gender} className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline" id="gender" required>
                                        <option value="">Pilih Jenis Kelamin</option>
                                        <option value="Male">Male</option>
                                        <option value="Female">Female</option>
                                        <option value="Non-Binary">Non-Binary</option>
                                    </select>
                                </div>
                                <div className="flex items-center justify-between">
                                    <button onClick={handleAddMember} className="bg-brand-lime text-brand-dark font-bold py-2 px-4 rounded-lg hover:bg-brand-lime-dark transition" type="button">
                                        Simpan
                                    </button>
                                    <button onClick={toggleModal} className="bg-gray-500 text-white font-bold py-2 px-4 rounded-lg hover:bg-gray-600 transition" type="button">
                                        Batal
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}


            </main>
        </div>
    );
};

export default Members;
